import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  X, MagnifyingGlass, UploadSimple, Camera, DownloadSimple,
  Star, StarHalf,
} from "@phosphor-icons/react";

/**
 * PhotoLibraryPicker — reusable modal that surfaces:
 *   1. The local `cms_photos` library (`GET /cms/photos`).
 *   2. The photopulse.pro public feed (`GET /photopulse/feed`).
 *
 * Editors can:
 *   • Search / filter by category
 *   • Upload new photos (computer + phone camera + gallery)
 *   • One-tap import any photopulse.pro photo into the local library
 *   • Pick a photo → `onSelect({ url, alt })` — used by RichEditor and cover-image picker.
 */

const CATEGORIES = [
  "sunsets", "plants", "flowers", "animals", "birds", "insects",
  "trees", "landscape", "rocks", "hills", "mountains",
  "rain", "snow", "hail", "clouds",
  "cityscape", "streets", "roads",
  "beach", "lake", "rivers", "wetlands",
  "agriculture", "wildlife", "forests", "other",
];

export default function PhotoLibraryPicker({ open, onClose, onSelect, defaultCategory = "" }) {
  const [tab, setTab] = useState("local");
  const [local, setLocal] = useState([]);
  const [remote, setRemote] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(defaultCategory);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importingId, setImportingId] = useState(null);

  const loadLocal = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set("category", category);
      params.set("limit", "60");
      const { data } = await http.get(`/cms/photos?${params}`);
      setLocal(data.photos || []);
    } catch (e) {
      toast.error("Failed to load library");
    } finally {
      setLoading(false);
    }
  };

  const loadRemote = async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/photopulse/feed?limit=40`);
      setRemote(data.photos || []);
    } catch (e) {
      toast.error("PhotoPulse feed unavailable");
      setRemote([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (tab === "local") loadLocal();
    else loadRemote();
    // eslint-disable-next-line
  }, [open, tab, category]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("Image must be under 20 MB"); return; }
    if (!category) { toast.error("Pick a category first"); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const { data } = await http.post(`/cms/photos`, {
            image_base64: reader.result,
            content_type: file.type,
            category,
            tags: [],
          });
          toast.success("Photo added to library");
          setLocal((l) => [data, ...l]);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (e) {
      setUploading(false);
    }
  };

  const importFromPhotopulse = async (photo) => {
    if (!category) { toast.error("Pick a category first"); return; }
    setImportingId(photo.id || photo.image_url);
    try {
      const { data } = await http.post(`/cms/photos/import-from-photopulse`, {
        image_url: photo.image_url,
        category,
        tags: photo.tags || [],
        caption: photo.title || photo.caption || null,
        credit: photo.photographer || photo.credit || "PhotoPulse.pro",
        location: photo.location || null,
        photopulse_id: photo.id || null,
      });
      toast.success("Imported to local library");
      setLocal((l) => [data, ...l]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImportingId(null);
    }
  };

  const pick = (url, alt) => {
    onSelect?.({ url, alt: alt || "" });
    onClose?.();
  };

  const filteredLocal = q
    ? local.filter((p) => (
        (p.caption || "").toLowerCase().includes(q.toLowerCase()) ||
        (p.tags || []).some((t) => t.includes(q.toLowerCase()))
      ))
    : local;

  const filteredRemote = q
    ? remote.filter((p) => (
        (p.title || p.caption || "").toLowerCase().includes(q.toLowerCase()) ||
        (p.tags || []).some((t) => (t || "").toLowerCase().includes(q.toLowerCase()))
      ))
    : remote;

  if (!open) return null;
  return (
    <div
      data-testid="photo-library-modal"
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-stretch md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
    >
      <div
        className="bg-[#FBF2E1] w-full max-w-[1100px] max-h-[100vh] md:max-h-[90vh] md:rounded-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center gap-3 border-b border-black/10 px-5 py-4">
          <div className="flex-1 min-w-0">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">CMS · Photo Library</div>
            <div className="font-serif-display text-xl leading-tight">Insert a photo</div>
          </div>
          <button onClick={onClose} data-testid="photo-library-close" className="rounded-full p-1.5 hover:bg-black/5">
            <X size={18} weight="bold" />
          </button>
        </header>

        {/* Tabs + controls */}
        <div className="border-b border-black/10 px-5 py-3 flex flex-wrap items-center gap-3">
          <div className="flex gap-1">
            <button
              onClick={() => setTab("local")}
              data-testid="photo-tab-local"
              className={
                "rounded-full px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase " +
                (tab === "local"
                  ? "bg-[color:var(--np-brand,#B85F2A)] text-white"
                  : "bg-white border border-black/15 hover:bg-black/5")
              }
            >Local library</button>
            <button
              onClick={() => setTab("photopulse")}
              data-testid="photo-tab-photopulse"
              className={
                "rounded-full px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase " +
                (tab === "photopulse"
                  ? "bg-[color:var(--np-brand,#B85F2A)] text-white"
                  : "bg-white border border-black/15 hover:bg-black/5")
              }
            >PhotoPulse.pro ↗</button>
          </div>
          <div className="flex-1 min-w-[240px] relative">
            <MagnifyingGlass size={13} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8B7F]" />
            <input
              data-testid="photo-search"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search caption / tag…"
              className="w-full rounded-full border border-black/15 bg-white pl-8 pr-3 py-1.5 text-sm"
            />
          </div>
          <select
            data-testid="photo-category"
            value={category} onChange={(e) => setCategory(e.target.value)}
            className="rounded-full border border-black/15 bg-white px-3 py-1.5 text-sm"
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {tab === "local" && (
            <>
              <label
                data-testid="photo-upload"
                className={
                  "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase cursor-pointer " +
                  (uploading
                    ? "bg-black/10 text-[#8E8B7F]"
                    : "bg-[color:var(--np-brand,#B85F2A)] text-white hover:opacity-90")
                }
              >
                <UploadSimple size={13} weight="bold" />
                {uploading ? "Uploading…" : "Upload"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => upload(e.target.files?.[0])}
                  data-testid="photo-upload-input"
                />
              </label>
              <label
                data-testid="photo-camera"
                className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase cursor-pointer hover:bg-black/5"
              >
                <Camera size={13} weight="bold" />
                Camera
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => upload(e.target.files?.[0])}
                  data-testid="photo-camera-input"
                />
              </label>
            </>
          )}
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center text-sm text-[#8E8B7F] py-16">Loading…</div>
          ) : tab === "local" ? (
            filteredLocal.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-sm text-[#8E8B7F]">No photos in the library yet.</div>
                <div className="text-xs text-[#8E8B7F] mt-2">Pick a category above and hit Upload.</div>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="photo-grid-local">
                {filteredLocal.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`photo-tile-${p.id}`}
                    onClick={() => pick(p.url, p.caption || "")}
                    className="text-left rounded-xl border border-black/10 bg-white overflow-hidden group hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-[4/3] bg-black/5 overflow-hidden">
                      <img src={p.url} alt={p.caption || ""} loading="lazy"
                           className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
                    </div>
                    <div className="p-2">
                      <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                        {p.category} {p.featured && "· ★"}
                      </div>
                      <div className="text-xs text-[#241209] line-clamp-2">{p.caption || p.credit || "—"}</div>
                    </div>
                  </button>
                ))}
              </div>
            )
          ) : (
            filteredRemote.length === 0 ? (
              <div className="text-center py-16 text-sm text-[#8E8B7F]">No PhotoPulse photos returned.</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="photo-grid-remote">
                {filteredRemote.map((p, i) => {
                  const url = p.image_url || p.thumb_url;
                  const id = p.id || url;
                  return (
                    <div key={id + i}
                         data-testid={`photo-remote-${p.id || i}`}
                         className="rounded-xl border border-black/10 bg-white overflow-hidden">
                      <div className="aspect-[4/3] bg-black/5 overflow-hidden">
                        <img src={url} alt={p.title || ""} loading="lazy" className="w-full h-full object-cover" />
                      </div>
                      <div className="p-2">
                        <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                          PhotoPulse
                        </div>
                        <div className="text-xs text-[#241209] line-clamp-2">{p.title || p.caption || "—"}</div>
                        <div className="mt-1.5 flex gap-1.5">
                          <button
                            data-testid={`photo-import-${p.id || i}`}
                            onClick={() => importFromPhotopulse(p)}
                            disabled={importingId === id || !category}
                            title={!category ? "Pick a category first" : "Copy into local library"}
                            className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-2 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase disabled:opacity-50"
                          >
                            <DownloadSimple size={10} weight="bold" />
                            {importingId === id ? "…" : "Import"}
                          </button>
                          <button
                            data-testid={`photo-remote-pick-${p.id || i}`}
                            onClick={() => pick(url, p.title || "")}
                            className="rounded-full border border-black/15 bg-white px-2 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-black/5"
                          >
                            Use once
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
